/*  MUI  */
import { Box, Divider } from '@mui/material';

/*  Shared  */
import { SIDEBAR_WIDTH } from 'application/Shared/Constants';
import { Browser } from 'application/Shared/Handlers';
import { SessionHelper } from 'application/Shared/Helpers';
import { Session } from 'application/Shared/types/session';

/* Hooks */
import { useIsOnTrialFromPartnerTenantInfo } from 'hooks/dataAccess/Session/isOnTrial';
import { useSession } from 'hooks/redux/Selectors/session';
import { useTenantInfoByTenantId } from 'hooks/redux/Selectors/tenants';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Components  */
import { SidebarAdminLinks } from './internal/SidebarAdminLinks';
import { SidebarGlobalLinks } from './internal/SidebarGlobalLinks';
import { SidebarGlobalMenuItems } from './internal/SidebarGlobalMenuItems';
import { SidebarHeader } from './internal/SidebarHeader';
import { SidebarHomeLink } from './internal/SidebarHomeLink';
import { SidebarPartnerLinks } from './internal/SidebarPartnerLinks';
import { SidebarReturnTo } from './internal/SidebarReturnTo';
import { SidebarUserMenuItems } from './internal/SidebarUserMenuItems';

const handleReturnToPartnerHub = ({
    tenant
}: {
    tenant: Session['tenant'];
}) => {
    Browser.handleTabTitle({ tenant });
};

interface SideBarProps {
    adminView?: boolean;
    globalView?: boolean;
    page?: string;
    partnerView?: boolean;
}

export const Sidebar = ({
    adminView,
    globalView,
    page,
    partnerView
}: SideBarProps): React.ReactNode => {
    const { t } = useTranslation();
    const session = useSession();

    const hubType = session?.sessionSettings?.hubType;
    const sessionHelper = useMemo(() => new SessionHelper(session), [session]);

    const { clientId } = useParams();
    const clientTenantInfo = useTenantInfoByTenantId(clientId);
    const client = clientTenantInfo?.tenant;
    const displayTrialBanner = useIsOnTrialFromPartnerTenantInfo();

    if (!session.tenant) {
        return null;
    }

    // FIXME: This can never be false since if session.tenant.settings?.security_logging is falsy, it'll just get flipped to true.
    const applicationLogging =
        session.tenant.settings?.security_logging || true;

    const renderGlobalLinks = globalView && session.tenant.admin;
    const renderPartnerLinks = sessionHelper.isPartner() && !!partnerView;
    const renderAdminLinks = !!adminView;

    const subheaderTitle = () => {
        if (renderGlobalLinks) {
            return t('Common.globalAdmin');
        }
        if (renderPartnerLinks) {
            return t('Common.partner_hub');
        }
        return t('Common.admin_hub');
    };

    const bannerHeight = '45px'; // Magic number, height of viewPricing banner, must match classname trialBannerMargin

    const sessionTenant = sessionHelper.getTenant();

    return (
        <Box
            data-testid="sidebar"
            display="flex"
            flexDirection="column"
            flex={1}
            id="mainViewSidebar"
            sx={{
                backgroundColor: 'sidebar.backgroundColor',
                // Display trial banner only happens at partner level
                height:
                    displayTrialBanner && renderPartnerLinks
                        ? `calc(100% - ${bannerHeight})`
                        : '100%',
                position: 'fixed',
                width: SIDEBAR_WIDTH
            }}
        >
            <Box data-testid="sidebarTopSection">
                {/* Top section goes here */}
                <SidebarHeader
                    client={client}
                    handleReturnToPartnerHub={() =>
                        handleReturnToPartnerHub({
                            tenant: sessionTenant
                        })
                    }
                    showClient={!sessionTenant.partner}
                    title={subheaderTitle()}
                />
            </Box>
            <Box
                data-testid="sidebarLinksSection"
                flex="1"
                style={{
                    overflowY: 'auto'
                }}
            >
                {/* Sidebar items go here, scrollable */}
                {renderGlobalLinks && <SidebarGlobalLinks page={page} />}
                {renderPartnerLinks && (
                    <SidebarPartnerLinks
                        hubType={hubType}
                        page={page}
                        session={session}
                    />
                )}
                {renderAdminLinks && (
                    <SidebarAdminLinks
                        applicationLogging={applicationLogging}
                        page={page}
                        session={session}
                    />
                )}

                {/*
                    If we're not rendering either the admin links or partner links, we'll
                    end up with a mostly empty sidebar. This will add some basic links to
                    bring the user back to their appropriate dashboard.
                */}
                {!renderGlobalLinks &&
                    !renderAdminLinks &&
                    !renderPartnerLinks && (
                        <SidebarHomeLink session={session} />
                    )}
            </Box>

            <Box
                data-testid="sidebarBottomItemsSection"
                pt="8px"
                sx={{ backgroundColor: 'sidebar.backgroundColorBottomSection' }}
            >
                {/* Bottom items, always visible, go here */}
                {!renderGlobalLinks && (
                    <>
                        <SidebarReturnTo
                            handleReturnToPartnerHub={() =>
                                handleReturnToPartnerHub({
                                    tenant: sessionTenant
                                })
                            }
                            renderAdminLinks={renderAdminLinks}
                        />
                        {sessionHelper.isGlobalAdmin() && (
                            <SidebarGlobalMenuItems />
                        )}
                        <Divider
                            sx={{
                                border: '1px solid',
                                borderColor: 'sidebar.divider',
                                opacity: 1
                            }}
                        />
                    </>
                )}
                <SidebarUserMenuItems session={session} page={page} />
            </Box>
        </Box>
    );
};
